import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Folder,
  File,
  Upload,
  FolderPlus,
  Download,
  Trash2,
  MoreHorizontal,
  Pencil,
  ChevronRight,
  Home,
  Cloud,
  CloudOff,
  RefreshCw,
  ArrowLeft,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import config from '../config';

const API = config.BACKEND_URL;

// External OneDrive link
const ONEDRIVE_EXTERNAL_LINK = "https://1drv.ms/f/c/371a6bb1f7723f27/IgAnP3L3sWsaIIA3x0kAAAAAAc7iqe7YnM8vFJgO98rAnS0?e=SFWaAl";

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '-';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getFileIcon = (item) => {
  if (item.is_folder) {
    return <Folder className="h-5 w-5 text-blue-500" />;
  }
  return <File className="h-5 w-5 text-gray-400" />;
};

export const Documents = () => {
  const { isAdmin } = useAuth();
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([{ prefix: '', name: 'Root' }]);
  
  // Dialog states
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form states
  const [newFolderName, setNewFolderName] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/s3/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      
      if (response.data.connected) {
        loadFiles('');
      }
    } catch (error) {
      setStatus({ connected: false, configured: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const loadFiles = async (prefix = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/s3/files`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { prefix }
      });
      
      setItems(response.data.items || []);
      setCurrentPrefix(prefix);
    } catch (error) {
      if (error.response?.status === 401) {
        setStatus({ connected: false, configured: true });
        toast.error('S3 session expired. Please check configuration.');
      } else {
        toast.error('Failed to load files');
      }
    }
  };

  const navigateToFolder = (folderPrefix, folderName) => {
    if (folderPrefix === '') {
      setBreadcrumbs([{ prefix: '', name: 'Root' }]);
    } else {
      // Check if navigating back
      const existingIndex = breadcrumbs.findIndex(b => b.prefix === folderPrefix);
      if (existingIndex >= 0) {
        setBreadcrumbs(breadcrumbs.slice(0, existingIndex + 1));
      } else {
        setBreadcrumbs([...breadcrumbs, { prefix: folderPrefix, name: folderName }]);
      }
    }
    loadFiles(folderPrefix);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/s3/folders`,
        { name: newFolderName, prefix: currentPrefix },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Folder created');
      setCreateFolderOpen(false);
      setNewFolderName('');
      loadFiles(currentPrefix);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('token');

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        await axios.post(
          `${API}/api/s3/files/upload?prefix=${encodeURIComponent(currentPrefix)}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }

    setUploading(false);
    loadFiles(currentPrefix);
    e.target.value = '';
  };

  const handleDownload = async (item) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/s3/files/${encodeURIComponent(item.key)}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', item.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleRename = async () => {
    if (!newItemName.trim() || !selectedItem) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/api/s3/files/${encodeURIComponent(selectedItem.key)}/rename`,
        { new_name: newItemName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Renamed successfully');
      setRenameDialogOpen(false);
      setSelectedItem(null);
      setNewItemName('');
      loadFiles(currentPrefix);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/s3/files/${encodeURIComponent(selectedItem.key)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      loadFiles(currentPrefix);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const openRenameDialog = (item) => {
    setSelectedItem(item);
    setNewItemName(item.name);
    setRenameDialogOpen(true);
  };

  const openDeleteDialog = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  // Not configured - show message
  if (!status?.configured || !status?.connected) {
    return (
      <div className="space-y-6" data-testid="documents-page">
        {/* External OneDrive Link Banner */}
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <CardContent className="py-4">
            <a 
              href={ONEDRIVE_EXTERNAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 font-medium"
              data-testid="onedrive-external-link"
            >
              <ExternalLink className="h-5 w-5" />
              Access Shared OneDrive Folder
              <span className="text-sm font-normal text-blue-600 dark:text-blue-400">(Opens in new tab)</span>
            </a>
          </CardContent>
        </Card>

        <div>
          <h1 className="text-3xl font-bold tracking-tight">Documents</h1>
        </div>

        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CloudOff className="h-5 w-5" />
              S3 Storage Not Available
            </CardTitle>
            <CardDescription>
              {status?.message || 'S3 storage is not configured. Please contact your system administrator.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isAdmin() && (
              <div className="p-4 bg-muted rounded-lg text-sm space-y-2">
                <p className="font-medium">Admin: Configure S3 in environment variables</p>
                <code className="block bg-background p-2 rounded text-xs">
                  S3_ENDPOINT_URL=http://your-s3-host:9000<br/>
                  S3_ACCESS_KEY=your_access_key<br/>
                  S3_SECRET_KEY=your_secret_key<br/>
                  S3_BUCKET_NAME=your_bucket_name<br/>
                  S3_REGION=us-east-1
                </code>
                <p className="text-muted-foreground">
                  Set these in your backend .env file and restart the server.<br/>
                  <span className="text-xs">For self-hosted S3 (MinIO, etc.), set S3_ENDPOINT_URL to your server address.</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Connected - show file browser
  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* External OneDrive Link Banner */}
      <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <CardContent className="py-4">
          <a 
            href={ONEDRIVE_EXTERNAL_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 font-medium"
            data-testid="onedrive-external-link"
          >
            <ExternalLink className="h-5 w-5" />
            Access Shared OneDrive Folder
            <span className="text-sm font-normal text-blue-600 dark:text-blue-400">(Opens in new tab)</span>
          </a>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Documents</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadFiles(currentPrefix)}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((crumb, index) => (
              <BreadcrumbItem key={crumb.prefix || 'root'}>
                {index > 0 && <BreadcrumbSeparator />}
                <BreadcrumbLink
                  onClick={() => navigateToFolder(crumb.prefix, crumb.name)}
                  className="cursor-pointer hover:text-foreground"
                >
                  {index === 0 ? <Home className="h-4 w-4" /> : crumb.name}
                </BreadcrumbLink>
              </BreadcrumbItem>
            ))}
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCreateFolderOpen(true)}
            data-testid="create-folder-btn"
          >
            <FolderPlus className="h-4 w-4 mr-2" />
            New Folder
          </Button>
          <label>
            <Button variant="default" disabled={uploading} asChild>
              <span className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload'}
              </span>
            </Button>
            <input
              type="file"
              multiple
              hidden
              onChange={handleUpload}
              disabled={uploading}
              data-testid="upload-input"
            />
          </label>
        </div>
      </div>

      {/* File list */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50%]">Name</TableHead>
              <TableHead>Modified</TableHead>
              <TableHead>Size</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentPrefix !== '' && (
              <TableRow
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => {
                  const parentIndex = breadcrumbs.length - 2;
                  if (parentIndex >= 0) {
                    navigateToFolder(breadcrumbs[parentIndex].prefix, breadcrumbs[parentIndex].name);
                  }
                }}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">..</span>
                  </div>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
            {items.length === 0 && currentPrefix === '' && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No files or folders. Upload files or create a folder to get started.
                </TableCell>
              </TableRow>
            )}
            {items
              .sort((a, b) => {
                // Folders first, then files
                if (a.is_folder && !b.is_folder) return -1;
                if (!a.is_folder && b.is_folder) return 1;
                return a.name.localeCompare(b.name);
              })
              .map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer hover:bg-muted/50"
                  data-testid={`file-row-${item.name}`}
                >
                  <TableCell
                    onClick={() => {
                      if (item.is_folder) {
                        navigateToFolder(item.key, item.name);
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {getFileIcon(item)}
                      <span className={item.is_folder ? 'font-medium' : ''}>
                        {item.name}
                      </span>
                      {item.is_folder && (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(item.modified_at)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.is_folder ? '-' : formatFileSize(item.size)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!item.is_folder && (
                          <DropdownMenuItem onClick={() => handleDownload(item)}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </DropdownMenuItem>
                        )}
                        {!item.is_folder && (
                          <DropdownMenuItem onClick={() => openRenameDialog(item)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => openDeleteDialog(item)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
        <DialogContent data-testid="create-folder-dialog">
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Enter a name for the new folder
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              id="folder-name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="New Folder"
              className="mt-2"
              data-testid="folder-name-input"
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateFolderOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder} disabled={saving} data-testid="create-folder-submit">
              {saving ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent data-testid="rename-dialog">
          <DialogHeader>
            <DialogTitle>Rename File</DialogTitle>
            <DialogDescription>
              Enter a new name
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new-name">New Name</Label>
            <Input
              id="new-name"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              className="mt-2"
              data-testid="rename-input"
              onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={saving} data-testid="rename-submit">
              {saving ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedItem?.is_folder ? 'Folder' : 'File'}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedItem?.name}"?
              {selectedItem?.is_folder && ' This will delete all contents inside.'}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
